import { createForm, useCreateFormStore } from '@components/form';
import { RelativeSpinner } from '@components/loading-spinner';
import { hideModal, Modal, useModal } from '@components/modal';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';

import { useFormTemplateJourneys, useMoveFormTemplate } from 'api/forms/hooks';
import { ListFormTemplate } from 'api/forms/requests';
import { useWorkspaces } from 'api/workspaces/hooks';

export const useMoveFormTemplateModal = () => useModal(MoveFormTemplateModal);

type MoveFormTemplateData = {
  workspace: string;
};

const Form = createForm<MoveFormTemplateData>();

export const MoveFormTemplateModal = ({ formTemplate }: { formTemplate: ListFormTemplate }) => {
  const { t } = useScopedTranslate(locale);
  const formStore = useCreateFormStore();

  const moveMutation = useMoveFormTemplate(formTemplate.shortId);
  const { data: workspaces } = useWorkspaces();
  const { data: journeysData } = useFormTemplateJourneys(formTemplate.shortId);

  const handleSubmit = async (data: MoveFormTemplateData) => {
    await moveMutation.mutateAsync(data.workspace);
    hideModal();
  };

  if (!workspaces || !journeysData) {
    return <RelativeSpinner />;
  }
  const isTooManyJourneys = journeysData.length > 0;

  return (
    <Modal>
      <Form store={formStore} onSubmit={handleSubmit}>
        <Modal.SimpleHeader title={t('title')} />
        <Modal.Content>
          <Stack space={6}>
            {isTooManyJourneys && <Text text={t('cantMoveDescription')} />}
            <Form.Select
              id="workspace"
              isDisabled={isTooManyJourneys}
              label={t('workspace')}
              options={workspaces
                .filter((workspace) => workspace.shortId !== formTemplate.workspace)
                .map((workspace) => ({
                  value: workspace.shortId,
                  label: workspace.title,
                }))}
            />
          </Stack>
        </Modal.Content>
        <Modal.Footer>
          <Form.Actions>
            <Form.Reset text={t('core.cancel')} variant="secondary" onClick={hideModal} />
            <Form.Submit
              text={t('confirm')}
              variant="primary-blue"
              tooltip={isTooManyJourneys ? t('cantMove') : ''}
              isDisabled={moveMutation.isLoading || isTooManyJourneys}
            />
          </Form.Actions>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export const locale = {
  en: {
    title: 'Move form',
    workspace: 'Workspace',
    confirm: 'Move form',
    cantMove: "Can't move forms in use",
    cantMoveDescription: "You can't move forms that are already in use",
    notPublishedDisabled: 'Cannot include travellers unless a journey is published',
  },
};
